import {
  UserAgent,
  DefaultFullscreenHelper,
  FullscreenHelper,
} from "@blacknut/javascript-sdk/dist";
import {
  ConsoleAppender,
  LogLevel,
  logD,
  logE,
  logI,
  logW,
  logger,
} from "@blacknut/logging/dist";
import debounce from "lodash.debounce";
import * as TransformSDP from "sdp-transform";
import { isSafari } from "../utils";
import { LOGGING_TAG, formatIceServers } from "../utils/logging";
import {
  AndroidPeerConnectionWrapper,
  HTMLVideoElementWrappper,
  PeerConnectionCommon,
  isHTMLVideoElememt,
} from "../utils/native";
import { NativeModule } from "../utils/native/NativeModule";
import { REMOVE_GET_ICE_CANDIDATE } from "../utils/version";
import { PlayerEvents, eventEmitter } from "./EventEmitter";
import { InputListener, MouseInputMode } from "./InputListener";
import { WebSocketManager } from "./WebSocketManager";
import { ErrorResponse, WebSocketNegotiation } from "./WebSocketNegotiation";

export declare type WindowMode = "windowed" | "fullscreen";
const TRACE_METRICS = false;
declare global {
  interface Navigator {
    keyboard?: {
      lock: () => Promise<void>;
      getLayoutMap: () => Promise<{ get: (key: string) => string }>;
    };
  }
}

declare global {
  interface Document {
    webkitFullscreenElement?: Element;
    webkitExitFullscreen: () => void;
  }
}

export const isErrorResponse = (data: any): data is ErrorResponse => {
  const res = !!data && "error" in data;
  return res;
};

export interface GameStreamMetricConnection {
  tags?: {
    localProtocol: RTCIceProtocol;
    localCandidateType: RTCIceCandidateType;
    remoteProtocol: RTCIceProtocol;
    remoteCandidateType: RTCIceCandidateType;
  };
  packetsSent?: number;
  packetsReceived?: number;
  bytesSent?: number;
  bytesReceived?: number;
  currentRoundTripTime?: number;
  availableOutgoingBitrate?: number;
}

const safeParseInt = (stat: any, key: string) => {
  if (stat[key] === undefined) {
    return undefined;
  }
  return typeof stat[key] === "string" ? parseInt(stat[key], 10) : (stat[key] as number);
};

const safeParseFloat = (stat: any, key: string) => {
  if (stat[key] === undefined) {
    return undefined;
  }
  return typeof stat[key] === "string" ? parseFloat(stat[key]) : (stat[key] as number);
};

// play effect does not seem to accept duration higher than a few seconds (5s ok, 10s not ok)
// so restart effect until prempted, e.g. until something else is requested
const playDualRumbleEffectTilPreempted = async (
  index: number,
  strongMagnitude: number,
  weakMagnitude: number,
) => {
  let maintain = true;
  while (maintain) {
    const gamepads = window.navigator.getGamepads();
    if (gamepads[index] && gamepads[index].vibrationActuator) {
      try {
        let e = await gamepads[index].vibrationActuator.playEffect("dual-rumble", {
          duration: 1000,
          startDelay: 0,
          strongMagnitude: strongMagnitude,
          weakMagnitude: weakMagnitude,
        });
        if (e === "preempted") {
          maintain = false;
        }
      } catch (e) {
        maintain = false;
      }
    } else {
      maintain = false;
    }
    if (maintain) {
      await new Promise((resolve) => setTimeout(resolve, 1));
    }
  }
};

export interface GameStreamMetric {
  video?: Record<string, number>;
  audio?: {
    decoding?: number;
    rendering?: number;
    latency?: number;
    receiveinterval?: number;
    rate?: number;
    pktLost?: number;
    pktTotal?: number;
    jitter?: number;
    bitrate?: number;
    bytesRecv?: number;
    pktRecv1?: number;
    pktRecv?: number;
    pktReject1?: number;
    pktRecv2?: number;
    pktReject2?: number;
    pktRecv4?: number;
    pktReject4?: number;
    pkt4DelayMin?: number;
    pkt4DelayMax?: number;
  };
  network: {
    rtt?: number;
  };
  connection?: GameStreamMetricConnection;
}

export class WebRTCClient {
  srvurl: string;
  token;
  private pc: PeerConnectionCommon;

  pcOptions;
  sdpOptions;

  stream?: MediaStream;
  videoElement?: HTMLVideoElement | HTMLVideoElementWrappper;
  videoTag: string;
  contentNode: HTMLElement;
  container?: HTMLElement;

  mediaConstraints: RTCOfferOptions;
  pcConfig: RTCConfiguration;
  webSocketManager?: WebSocketManager;
  inputListener: InputListener;

  webSocketNegotiation: WebSocketNegotiation;

  metricsTimeout: NodeJS.Timeout;

  private _gatheringTimeout: NodeJS.Timeout;

  private _iceCandidates = [];

  /**
   * Embedded on android
   */
  private _embedded = false;
  public get embedded(): boolean {
    return this._embedded;
  }
  public set embedded(v: boolean) {
    this._embedded = v;
  }
  private _streamingSize: {
    width: number;
    height: number;
    upscaleSmallerVideo: boolean;
  } = {
    width: 1280,
    height: 720,
    upscaleSmallerVideo: false,
  };
  public get streamingSize(): {
    width: number;
    height: number;
    upscaleSmallerVideo: boolean;
  } {
    return this._streamingSize;
  }
  public set streamingSize(v: {
    width: number;
    height: number;
    upscaleSmallerVideo: boolean;
  }) {
    this._streamingSize = v;
  }

  private _mouseEnabled = true;
  public get mouseEnabled(): boolean {
    return this._mouseEnabled;
  }
  public set mouseEnabled(v: boolean) {
    this._mouseEnabled = v;
  }

  private _filteredMimeTypes: string[] | undefined;
  public get filteredMimeTypes(): string[] | undefined {
    return this._filteredMimeTypes;
  }
  public set filteredMimeTypes(v: string[] | undefined) {
    this._filteredMimeTypes = v;
  }

  private _hasPlayed: boolean;
  public get hasPlayed(): boolean {
    return this._hasPlayed;
  }
  public set hasPlayed(v: boolean) {
    this._hasPlayed = v;
  }

  private _autoplay = false;
  public get autoplay(): boolean {
    return this._autoplay;
  }
  public set autoplay(v: boolean) {
    this._autoplay = v;
  }

  private _privateRelay: boolean | undefined;
  public get privateRelay(): boolean | undefined {
    return this._privateRelay;
  }
  public set privateRelay(v: boolean | undefined) {
    this._privateRelay = v;
  }

  private _acquireMicro: boolean | undefined;
  public get acquireMicro(): boolean | undefined {
    return this._acquireMicro;
  }
  public set acquireMicro(v: boolean | undefined) {
    this._acquireMicro = v;
  }

  private _useDesktopCrop: boolean | undefined;
  public get useDesktopCrop(): boolean | undefined {
    return this._useDesktopCrop;
  }
  public set useDesktopCrop(v: boolean | undefined) {
    this._useDesktopCrop = v;
  }

  private _reconnectInputCount = 5;
  public get reconnectInputCount(): number {
    return this._reconnectInputCount;
  }
  public set reconnectInputCount(v: number) {
    this._reconnectInputCount = v;
  }

  private _mouseInputMode: MouseInputMode = "absolute";
  public get mouseInputMode(): MouseInputMode {
    return this._mouseInputMode;
  }
  public set mouseInputMode(v: MouseInputMode) {
    this._mouseInputMode = v;
  }

  private _mute = false;
  public get mute(): boolean {
    return this._mute;
  }
  public set mute(v: boolean) {
    this._mute = v;
  }

  private _streamId: string | undefined;
  public get streamId(): string | undefined {
    return this._streamId;
  }
  public set streamId(v: string | undefined) {
    this._streamId = v;
  }

  private _onFatalErrorResponse?: (response: ErrorResponse) => void;
  public get onFatalErrorResponse(): ((response: ErrorResponse) => void) | undefined {
    return this._onFatalErrorResponse;
  }
  public set onFatalErrorResponse(v: ((response: ErrorResponse) => void) | undefined) {
    this._onFatalErrorResponse = v;
  }

  private _onDisconnect: () => void;
  public get onDisconnect(): () => void {
    return this._onDisconnect;
  }
  public set onDisconnect(v: () => void) {
    this._onDisconnect = v;
  }

  private debouncedResize?: () => void;

  public lastFramesDecodedTimestampSecond: number | undefined;

  private _preferedWindowMode: WindowMode = "fullscreen";
  public get preferedWindowMode(): WindowMode {
    return this._preferedWindowMode;
  }
  public set preferedWindowMode(v: WindowMode) {
    this._preferedWindowMode = v;
  }

  private _statsInterval = 5000;
  public get statsInterval(): number {
    return this._statsInterval;
  }
  public set statsInterval(v: number) {
    this._statsInterval = v;
  }

  private _fullscreenHelper: FullscreenHelper | undefined;
  public get fullscreenHelper(): FullscreenHelper | undefined {
    return this._fullscreenHelper;
  }
  public set fullscreenHelper(v: FullscreenHelper | undefined) {
    this._fullscreenHelper = v;
  }

  private _onFullscreenChange?: (fullscreen: boolean) => void;
  private _onVisibilityChange?: () => void;

  private _userAgent: UserAgent;
  public get userAgent(): UserAgent {
    return this._userAgent;
  }
  public set userAgent(v: UserAgent) {
    this._userAgent = v;
  }

  constructor(
    contentNode,
    srvurl,
    token,
    pcConfig,
    videoTag,
    webSocketNegotiation,
    sdpOptions?,
  ) {
    this.webSocketNegotiation = webSocketNegotiation;

    // Dom information
    this.contentNode = contentNode;
    this.videoTag = videoTag;

    // WebRTCClient params
    this.srvurl =
      srvurl ||
      location.protocol +
        "//" +
        window.location.hostname +
        ":" +
        window.location.port +
        "/";
    this.token = token;
    this.pc = null;

    // WebRTCClient Options

    this.pcOptions = { optional: [{ DtlsSrtpKeyAgreement: true }] };
    this.sdpOptions = sdpOptions || {
      audio: {
        stereo: 1,
        cbr: 1,
        usedtx: 0,
      },
    };
    this.pcConfig = pcConfig;
    this._onFullscreenChange = this.onFullscreenChange.bind(this);
    this._onVisibilityChange = this.onVisibilityChange.bind(this);

    if (!logger.configured) {
      logger.configure({
        level: LogLevel.LEVEL_DEBUG,
        appenders: [new ConsoleAppender()],
      });
    }
  }

  private onFullscreenChange = async (fullscreen: boolean) => {
    logD(LOGGING_TAG, "onFullscreenChange %o", fullscreen);
    if (fullscreen) {
      if (!isSafari()) {
        this.requestPointerLock();
      }
    } else if (!!!navigator.keyboard) {
      // inject the esc key that we lost when user exited fullscreen on
      // navigators that do not support keyboard lock of esc (safari/firefox)
      this.inputListener.injectEscapeSequence();
      this.exitPointerLock();
    } else {
      this.exitPointerLock();
    }
  };

  private onFocus = async () => {
    const fullscreen = await this.fullscreenHelper.isFullscreen();
    logD(
      LOGGING_TAG,
      "on Focus %o vs %o: ",
      this.timeAtBlur,
      this.timeAtPointerLockChange,
      this.timeAtPointerLockChange - this.timeAtBlur,
    );
    if (
      this.timeAtPointerLockChange - this.timeAtBlur < 1000 &&
      fullscreen &&
      this.preferedWindowMode !== "windowed"
    ) {
      this.requestPointerLock();
    }
  };

  private onBlur = () => {
    logD(LOGGING_TAG, "Blur %o", document.pointerLockElement);
    this.timeAtBlur = Date.now();
  };

  private onVisibilityChange = async () => {
    const fullscreen = await this.fullscreenHelper.isFullscreen();
    logD(LOGGING_TAG, "onVisibilityChange", document.visibilityState, fullscreen);
    if (
      document.visibilityState === "visible" &&
      fullscreen &&
      this.preferedWindowMode !== "windowed"
    ) {
      this.requestPointerLock();
    }

    if (document.visibilityState === "visible") {
    } else {
    }
  };

  private requestPointerLock = () => {
    if (this.videoElement && isHTMLVideoElememt(this.videoElement)) {
      logD(LOGGING_TAG, "Request pointer lock on %o", this.videoElement);
    }
    if (isSafari()) return; // safari has its own mechanism&lifetime, see below
    if (!this.videoElement) return;
    if (this.mouseInputMode === "absolute") {
      logW(LOGGING_TAG, "Pointer lock on absolute. Ignore");
      return;
    }

    if (document.pointerLockElement === this.videoElement) {
      return;
    } else if (this.videoElement.requestPointerLock) {
      //according to mdn must return promise, but on sky live, it returns undefined
      //https://developer.mozilla.org/en-US/docs/Web/API/Element/requestPointerLock
      const p = this.videoElement.requestPointerLock() as unknown as Promise<void>;
      if (p) {
        p.then(() => {
          logD(LOGGING_TAG, "Pointer lock OK");
        }).catch((e) => {
          logE(LOGGING_TAG, "Caught error on pointer lock: %o", e);
        });
      }
    } else {
      logE(LOGGING_TAG, "no pointer lock API");
    }
  };

  private exitPointerLock = () => {
    if (document.exitPointerLock) {
      document.exitPointerLock();
      logD(LOGGING_TAG, "exitpointerlock");
    } else {
      logE(LOGGING_TAG, "no pointer lock API");
    }
  };

  // Method inspired by Jimbly/pointer_lock_wrap_safari
  // The direct method never works so it goes to async method while
  // still needing user input. Also patches pointerlock being set to null
  // right after accepting the video node - likely related to fullscreen
  // change at the same time.
  // Try direct and async for 1 sec
  private timeStartedSafariPointerLock: number | undefined;
  private timerPointerLockRetry: number | undefined;
  private safariRequestPointerLock = async () => {
    const fullscreen = await this.fullscreenHelper.isFullscreen();
    if (
      this.videoElement &&
      null === document.pointerLockElement &&
      (fullscreen || this.preferedWindowMode === "windowed")
    ) {
      this.videoElement.requestPointerLock();
    }
    if (undefined == this.timerPointerLockRetry) {
      this.timeStartedSafariPointerLock = Date.now();
      logD(LOGGING_TAG, "starting safarirequestpointerlock");
    }
    if (Date.now() - this.timeStartedSafariPointerLock < 1000) {
      this.timerPointerLockRetry = window.setTimeout(() => {
        this.safariRequestPointerLock();
      }, 1);
    } else {
      this.timeStartedSafariPointerLock = undefined;
      this.timerPointerLockRetry = undefined;
      logD(
        LOGGING_TAG,
        "completed safarirequestpointerlock",
        document.pointerLockElement,
      );
    }
  };

  public onEndUserPlay() {
    // handle safari use case and start pointer lock
    if (isSafari() && this.mouseInputMode === "relative") {
      this.safariRequestPointerLock();
    } else if (
      this.mouseInputMode === "relative" &&
      this.preferedWindowMode === "windowed"
    ) {
      this.requestPointerLock();
    }
  }
  public onEndUserResume() {
    this.onEndUserPlay();
  }
  public onEndUserRequestFullscreen() {
    if (
      isSafari() &&
      this.mouseInputMode === "relative" &&
      this.preferedWindowMode === "windowed"
    ) {
      this.safariRequestPointerLock();
    }
  }

  // When switch windows using Alt+TAB, a pointer lock change event is sent before the blur
  // In order to restore pointer lock check that time elapsed between pointerlockchange and blur is < to 3s
  private timeAtPointerLockChange: number | undefined;
  private timeAtBlur: number | undefined;

  private pointerlockchange = async () => {
    const fullscreen = await this.fullscreenHelper.isFullscreen();
    if (!!!document.pointerLockElement) {
      this.timeAtPointerLockChange = Date.now();
      if (this.preferedWindowMode === "windowed") {
        // inject the esc key that we lost when user released pointer lock
        if (this.timeStartedSafariPointerLock == undefined) {
          this.inputListener.injectEscapeSequence();
          this.inputListener.stopInputsPropagation();
        }
      }
    } else {
      // This collides with a standard exit of fullscreen on firefox, so we must remove pointerlock
      // from here, when not fullscreen anymore
      if (!fullscreen && this.preferedWindowMode !== "windowed") {
        this.exitPointerLock();
      }
      // windowed mode relatif, unmute inputs
      if (this.mouseInputMode === "relative" && this.preferedWindowMode === "windowed") {
        this.inputListener.startInputsPropagation();
      }
    }
    if (isSafari()) {
      // When not trying to set pointer lock, lock has been lost for
      // another reason, like switching app. Trigger fullscreen off
      // to go to same scenario as user exited fullscreen with esc key
      if (
        !!!document.pointerLockElement &&
        undefined === this.timeStartedSafariPointerLock &&
        fullscreen
      ) {
        logI(LOGGING_TAG, "safari lost pointerlock, exiting fullscreen");
        this.fullscreenHelper.exitFullscreen();
      }
    }
    logD(LOGGING_TAG, "pointerlockchange", document.pointerLockElement, document.hidden);
  };
  private pointerlockerror = (e) => {
    logD(LOGGING_TAG, "pointerlockerror", e);
  };

  private _connect() {
    try {
      if (!NativeModule.module) {
        NativeModule.init(this.embedded);
      }
      this.mediaConstraints = {
        offerToReceiveVideo: true,
        offerToReceiveAudio: !this.mute,
      };

      this.pc = this.createPeerConnection();
      logD(LOGGING_TAG, "Created RTCPeerConnnection with peer id %o", this.pc.peerid);

      // create Offer
      logD(LOGGING_TAG, "Add audio transceiver");
      this.pc.addTransceiver("audio", {
        direction: "recvonly",
      });
      logD(LOGGING_TAG, "Add video transceiver");
      this.pc.addTransceiver("video", { direction: "recvonly" });

      logI(LOGGING_TAG, "Create offer with constraints %o", this.mediaConstraints);

      const p = this.pc.createOffer(this.mediaConstraints);
      p.then((offer: RTCSessionDescriptionInit) => {
        this.onOfferCreated(offer);
      }).catch((error) => {
        this.onCreatedError(error);
      });
    } catch (e) {
      //Not sure we will ever be here, so leave disconnect as is, and do no try to reconnect
      logE(LOGGING_TAG, "Connect error: %o", e);
      eventEmitter.emit(PlayerEvents.ERROR, e);
      this.disconnect();
    }
  }

  connect() {
    logD(LOGGING_TAG, "In Connect");
    if (this.pc) {
      logW(LOGGING_TAG, "Already connected");
      return;
    }
    if (!this.fullscreenHelper) {
      this.fullscreenHelper = new DefaultFullscreenHelper();
    }
    this.fullscreenHelper.addFullcreenChangeListener(this._onFullscreenChange);
    document.addEventListener("pointerlockchange", this.pointerlockchange);
    document.addEventListener("pointerlockerror", this.pointerlockerror);
    document.addEventListener("visibilitychange", this._onVisibilityChange);
    document.addEventListener("focus", this.onFocus);
    document.addEventListener("blur", this.onBlur);
    window.addEventListener("focus", this.onFocus);
    window.addEventListener("blur", this.onBlur);

    if (
      (isSafari() && navigator.mediaDevices && this.privateRelay) ||
      (navigator.mediaDevices && this.acquireMicro)
    ) {
      try {
        navigator.mediaDevices
          .getUserMedia({ audio: true, video: false })
          .then((res) => {
            logD(LOGGING_TAG, "getUserMedia OK");
            this._connect();
            logD(LOGGING_TAG, "Stopping microphone ..");
            res.getAudioTracks().forEach((t) => {
              t.stop();
            });
          })
          .catch((err) => {
            logE(LOGGING_TAG, "getUserMedia error", err);
            this._connect();
          });
      } catch (e) {
        logE(LOGGING_TAG, "getUserMedia error", e);
        this._connect();
      }
    } else {
      this._connect();
    }
  }

  private postOffer(offer: any) {
    this.webSocketNegotiation.postData(
      "call",
      {
        body: offer,
        peerid: this.pc.peerid,
      },
      (data) => this.onReceiveCall(data),
    );
  }
  private onOfferCreated(offer: RTCSessionDescriptionInit) {
    logI(LOGGING_TAG, "onOfferCreated %o", offer);
    try {
      offer["sdp"] = this.parseSdp(offer.sdp);

      this.pc
        .setLocalDescription(offer as RTCSessionDescription)
        .then(() => {
          logD(LOGGING_TAG, "Local description set");
          this.postOffer(offer);
        })
        .catch((error) => {
          logE(LOGGING_TAG, "Caught error %o", error);
          eventEmitter.emit(PlayerEvents.ERROR, error);
          this.disconnect();
        });
    } catch (error) {
      logE(LOGGING_TAG, "Caught error %o", error);
      eventEmitter.emit(PlayerEvents.ERROR, error);
      this.disconnect();
    }
  }

  private onCreatedError(error) {
    logE(LOGGING_TAG, "Create offer error: %o" + JSON.stringify(error));
    throw error;
  }

  /**
   * SDP modification between createOffer() and setLocalDescription() is explicitly forbidden.
   * What is allowed is that you can modify the SDP to reduce the capabilities before sending it to the answerer.
   * So you would be allowed for example to reduce the value of an existing maxaveragebitrate parameter after calling setLocalDescription, but before sending it to the other side.
   * @param sdp
   */
  private parseSdp(sdp: string) {
    logI(LOGGING_TAG, "Input sdp %o", sdp);
    if (sdp === "t= 0 0") {
      logE(LOGGING_TAG, "Invalid sdp: %o", sdp);
    }
    const offer = TransformSDP.parse(sdp);

    if (!offer.media || offer.media.length === 0) {
      logE(LOGGING_TAG, "No media on offer %o", offer);
      throw "Internal error";
    }

    logI(LOGGING_TAG, "Parsed sdp %o", offer);
    delete (offer as any).extmapAllowMixed;
    if (offer.invalid) {
      offer.invalid = offer.invalid.filter((e) => e.value !== "extmap-allow-mixed");
    }

    //DEBUG Delete VPx
    if (this.filteredMimeTypes && this.filteredMimeTypes.length > 0) {
      for (const m of offer.media) {
        const filteredCodecs = this.filteredMimeTypes
          .filter((mime) => {
            return mime.startsWith(m.type);
          })
          .map((mime) => mime.substring(m.type.length + 1).toLocaleLowerCase());
        const payloadsToDelete: number[] = [];
        m.rtp.forEach((x) => {
          if (filteredCodecs.indexOf(x.codec.toLocaleLowerCase()) !== -1) {
            payloadsToDelete.push(x.payload);
          }
        });

        // Parse fmtp. Add to payloadsToDelete the ones with apt=X
        const fmtp = m.fmtp;
        fmtp.forEach((x) => {
          let match = false;
          for (const payload of payloadsToDelete) {
            if (x.config === `apt=${payload}`) {
              match = true;
              break;
            }
          }
          if (match) {
            payloadsToDelete.push(x.payload);
          }
        });

        // Now filter rtp
        const filter = ({ payload }: { payload: number }) => {
          return payloadsToDelete.indexOf(payload) === -1;
        };
        m.rtp = m.rtp.filter(filter);
        m.fmtp = m.fmtp.filter(filter);
        m.rtcpFb = m.rtcpFb.filter(filter);
        let payloads = m.payloads;
        payloadsToDelete.forEach(function (element) {
          const re = new RegExp(element + " ", "gi");
          payloads = payloads.replace(re, "");
        });
        m.payloads = payloads;
      }
    }

    logI(LOGGING_TAG, "Final sdp %o", offer);
    const s = TransformSDP.write(offer);
    logI(LOGGING_TAG, "Final sdp out %o", s);

    return s;
  }

  private deleteVideoElement() {
    if (this.videoElement) this.videoElement.srcObject = null;

    // Remove Video element
    logD(LOGGING_TAG, "Delete video element");
    if (this.videoElement && isHTMLVideoElememt(this.videoElement)) {
      this.videoElement.parentElement.removeChild(this.videoElement);
    }
    this.videoElement = null;

    if (this.container) {
      this.container.parentElement.removeChild(this.container);
    }
    this.container = null;
  }

  private closePeerConnexion() {
    // close and delete PeerConnection
    if (this.pc) {
      logI(LOGGING_TAG, "Closing peer conx");
      this.pc.close();
      this.pc = null;
    }
  }
  reconnect() {
    logD(LOGGING_TAG, "Reconnect ....");

    if (this._gatheringTimeout !== undefined) {
      logD(LOGGING_TAG, "Clearing gathering timeout");
      clearTimeout(this._gatheringTimeout);
      this._gatheringTimeout = undefined;
    }
    this.deleteVideoElement();
    this.closePeerConnexion();
    if (this.metricsTimeout !== undefined) {
      clearTimeout(this.metricsTimeout);
      this.metricsTimeout = undefined;
    }

    if (this.debouncedResize !== undefined) {
      window.removeEventListener("resize", this.debouncedResize);
      this.debouncedResize = undefined;
    }

    if (this.webSocketManager) {
      this.webSocketManager.stop(true);
      this.webSocketManager = undefined;
    } else {
      logD(LOGGING_TAG, "Input socket already closed...");
    }

    this.unbindInputs();
    this.removeEventsListeners();
    this.webSocketNegotiation.close();
  }
  /**
   * Disconnect a WebRTC Stream and clear videoElement source
   */

  private inDisconnect = false;

  disconnect(hangup?: boolean) {
    this.inDisconnect = true;
    logI(LOGGING_TAG, "In disconnect: hangup=%o", hangup);

    if (this._gatheringTimeout !== undefined) {
      logD(LOGGING_TAG, "Clearing gathering timeout");
      clearTimeout(this._gatheringTimeout);
      this._gatheringTimeout = undefined;
    }

    this.deleteVideoElement();

    // Desactivate inputlistener
    this.reconnectInputCount = 0; // do not try to reconnect
    this.unbindInputs();

    // Send 'exit;' inpult to server and close websocket input
    if (this.webSocketManager) {
      this.webSocketManager.stop(false);
      this.webSocketManager = null;
    }

    // close and delete PeerConnection
    if (this.pc) {
      try {
        // Send Hangup on negociation websocket to be closed by server
        this.webSocketNegotiation.postData("hangup", { peerid: this.pc.peerid }, () => {
          logD(LOGGING_TAG, "Server disconnection ok");
        });
      } catch (e) {
        logD(LOGGING_TAG, "Failure close peer connection", e);
      }
      // close and delete PeerConnection
      this.closePeerConnexion();
    }
    if (this.metricsTimeout !== undefined) {
      clearTimeout(this.metricsTimeout);
      this.metricsTimeout = undefined;
    }

    if (this.debouncedResize !== undefined) {
      window.removeEventListener("resize", this.debouncedResize);
      this.debouncedResize = undefined;
    }
    this.webSocketNegotiation.close();

    this.removeEventsListeners();

    eventEmitter.emit(PlayerEvents.DISCONNECT, hangup ? "userExit" : undefined);
    eventEmitter.offAll();
  }

  removeEventsListeners() {
    document.removeEventListener("pointerlockchange", this.pointerlockchange);
    document.removeEventListener("pointerlockerror", this.pointerlockerror);
    document.removeEventListener("visibilitychange", this._onVisibilityChange);
    document.removeEventListener("focus", this.onFocus);
    document.removeEventListener("blur", this.onBlur);
    window.removeEventListener("focus", this.onFocus);
    window.removeEventListener("blur", this.onBlur);
    this._fullscreenHelper.removeFullscreenChangeListener(this._onFullscreenChange);
  }

  /**
   * Inputs
   */
  createInputs() {
    logD(LOGGING_TAG, "Create input socket at url %o", this.srvurl);
    this.webSocketManager = new WebSocketManager({
      url: `${this.srvurl}${this.token ? `?token=${this.token}` : ""}`,
      onOpen: (client) => {
        logD(LOGGING_TAG, "Input opened: %o", client.id);
        this.reconnectInputCount = 5;
      },
      onClose: (client) => {
        logD(
          LOGGING_TAG,
          "Input %o socket closed. Reconnect: %o",
          client.id,
          this.reconnectInputCount,
        );
        if (this.reconnectInputCount !== 0) {
          this.reconnectInputCount--;
          setTimeout(() => {
            logD(LOGGING_TAG, "Recreate input socket");
            this.createInputs();
          }, 1000);
        }
      },
      onMessage: (event) => {
        this.onInputMessage(event);
      },
    });
    this.inputListener = new InputListener(this.webSocketManager);
    this.inputListener.mouseInputMode = this.mouseInputMode;
    this.inputListener.element = this.videoElement;
    this.inputListener.userAgent = this.userAgent;
    if (this.mouseEnabled && this.videoElement) {
      this.inputListener.mouseInit();
    }
    if (this.videoElement) {
      this.inputListener.touchInit();
    }

    this.bindInputs();
  }

  bindInputs() {
    this.inputListener.init();
  }
  unbindInputs() {
    if (this.inputListener) {
      this.inputListener.delete();
    }
  }

  bindKeyboard() {
    if (this.inputListener) {
      this.inputListener.initKeyBoard();
    }
  }
  unbindKeyboard() {
    if (this.inputListener) {
      this.inputListener.deleteKeyBoard();
    }
  }

  /*
   * create RTCPeerConnection
   */
  private createPeerConnection(): PeerConnectionCommon {
    const peerId = Math.floor(Math.random() * 100) + 1;

    let streamId = this.streamId;
    if (!this.streamId) {
      logW(LOGGING_TAG, "No streamId provided, compute from input url: %o", this.srvurl);
      // try to compute using arg url
      const url = new URL(this.srvurl);
      streamId = url.pathname.slice(1);
    }

    logI(
      LOGGING_TAG,
      "createPeerConnection with config: %o for stream: %o",
      { ...this.pcConfig, iceServers: formatIceServers(this.pcConfig.iceServers) },
      streamId,
    );

    // FIXME if else block can be simplified (to my mind)
    if (this.embedded && "android" in window) {
      logD(LOGGING_TAG, "Create android peer conx");
      const wrapper = NativeModule.module.createPeerConnection(
        this.pcConfig,
        peerId,
        streamId,
      ) as AndroidPeerConnectionWrapper;
      wrapper.onIceCandidate(this.onIceCandidate.bind(this));
      wrapper.onReady(() => {
        eventEmitter.emit(PlayerEvents.CONNECT, {});

        setTimeout(() => {
          this.videoElement = NativeModule.module.createHTMLVideoElementWrapper();

          this.lastFramesDecodedTimestampSecond = new Date().getTime() / 1000;
          this.initMetrics();
          this.createInputs();
          eventEmitter.emit(PlayerEvents.IS_PLAYING);
        }, 50);
      });
      wrapper.onPause(() => {
        logD(LOGGING_TAG, "onPause");
        if (this.metricsTimeout !== undefined) {
          clearTimeout(this.metricsTimeout);
          this.metricsTimeout = undefined;
        }
      });
      wrapper.onResume(() => {
        logD(LOGGING_TAG, "onResume");
        this.onDisconnect();
      });
      return wrapper;
    }

    logD(LOGGING_TAG, "Create web peer conx");
    const pc: RTCPeerConnection & { peerid?: number } = new RTCPeerConnection(
      this.pcConfig,
    );
    this._iceCandidates = [];
    pc.onicecandidate = (evt) => this.onIceCandidate(evt);

    pc.ontrack = (evt) => this.onAddTrack(evt);
    pc.oniceconnectionstatechange = (evt) => this.onIceConnectionStateChange(evt);
    pc.onnegotiationneeded = (evt) => {
      logD(LOGGING_TAG, "onnegotiationneeded %o", evt);
    };
    pc.onicecandidateerror = (evt: any) => {
      logE(
        LOGGING_TAG,
        "onicecandidateerror %o %o (%o: %o) at %o",
        evt,
        evt.address,
        evt.errorCode,
        evt.errorText,
        evt.url,
      );
    };

    // Fix candidate never null on pwa when switching network
    // Also happens when stun server cannot be targeted
    pc.onicegatheringstatechange = () => {
      logD(LOGGING_TAG, "onicegatheringstatechange %o", pc.iceGatheringState);
      if ("complete" === pc.iceGatheringState) {
        this.onIceCandidateEnded();
      } else if ("gathering" == pc.iceGatheringState) {
        this._gatheringTimeout = setTimeout(() => {
          logW(LOGGING_TAG, "Gathering setTimeout");
          this.onIceCandidateEnded();
        }, 5000);
      }
    };
    pc.onsignalingstatechange = (event) => {
      logD(LOGGING_TAG, "onsignalingstatechange %o", event);
    };

    pc.peerid = peerId;

    return pc as PeerConnectionCommon;
  }

  /*
   * RTCPeerConnection IceCandidateStateChange callback
   */
  private prevIceConnectionState: string;

  private onIceConnectionStateChange(evt) {
    const iceState = evt.target.iceConnectionState;
    logI(LOGGING_TAG, "onIceConnectionStateChange  state: %o", iceState);

    switch (iceState) {
      case "failed":
        // https://blacknut.atlassian.net/browse/CA-3591
        // for some reason (iOS ???) ice state can transiion from completed to failed
        // if this occurs, just restart the whome process instead of throwing an error
        if (this.prevIceConnectionState === "connected") {
          logW(LOGGING_TAG, "Transition from completed to failed, restart connection");
          this.onDisconnect();
        } else {
          eventEmitter.emit(PlayerEvents.ERROR, "Failed to find an ice candidate");
        }
        break;
      case "closed":
      case "disconnected":
        this.onDisconnect();
        break;
      case "connected":
        const pair = this.getSelectedRTCIceCandidatePair();
        if (pair) {
          eventEmitter.emit(PlayerEvents.CONNECT, {
            pair,
            candidates: this._iceCandidates,
          });
          logD(
            LOGGING_TAG,
            "Selected ice candidate pair %o/%o",
            pair.local,
            pair.remote,
          );
        } else {
          // no senders or no API (firefox) or uncomplete API (safari)
          eventEmitter.emit(PlayerEvents.CONNECT, {});
        }
        if (this.autoplay) {
          this.play();
        }
        break;
    }
    this.prevIceConnectionState = iceState;
  }

  private getSelectedRTCIceCandidatePair() {
    if (!this.pc) {
      return undefined;
    }
    const senders = this.pc.getSenders();
    if (senders.length > 0) {
      const iceTransport = senders[0].transport && senders[0].transport.iceTransport;
      if (iceTransport && !!iceTransport.getSelectedCandidatePair) {
        const pair = iceTransport.getSelectedCandidatePair();
        return pair;
      }
    }
    return undefined;
  }

  private onInputMessage(event: MessageEvent) {
    // parse the event data
    if (event.data) {
      const msg = JSON.parse(event.data);
      if (msg) {
        // handle gamepad effect that contains haptic feedback information
        if (
          msg.device &&
          msg.device.startsWith("gamepad") &&
          msg.effect &&
          window.navigator.getGamepads()
        ) {
          let gamepadIndex = msg.device.split("gamepad");
          if (!gamepadIndex || !gamepadIndex[1]) {
            return;
          }
          let index = parseInt(gamepadIndex[1]);
          if (isNaN(index)) {
            return;
          }
          index = index - 1; // first player is player one...
          if (msg.effect.strongmag !== undefined && msg.effect.weakmag !== undefined) {
            const gamepads = window.navigator.getGamepads();
            if (gamepads[index] && gamepads[index].vibrationActuator) {
              // check we support the effect - only dual-rumble at this time
              if (gamepads[index].vibrationActuator.type !== msg.effect.type) {
                return;
              }
              if (msg.effect.strongmag !== 0 || msg.effect.weakmag !== 0) {
                // play effect until preempted
                let p = playDualRumbleEffectTilPreempted(
                  index,
                  msg.effect.strongmag / 255.0,
                  msg.effect.weakmag / 255.0,
                );
                p.then(() => {}).catch((e) => {
                  logI(LOGGING_TAG, "error playing effect %o", e);
                });
              } else {
                // stop vibration with available method
                if (gamepads[index].vibrationActuator.reset) {
                  gamepads[index].vibrationActuator
                    .reset()
                    .then(() => {})
                    .catch((error) => {
                      logI(LOGGING_TAG, "stop effect error %o", error);
                    });
                } else {
                  let p = gamepads[index].vibrationActuator.playEffect("dual-rumble", {
                    duration: 100,
                    startDelay: 0,
                    strongMagnitude: 0,
                    weakMagnitude: 0,
                  });
                  p.then(() => {}).catch((error) => {
                    logI(LOGGING_TAG, "stop effect error %o", error);
                  });
                }
              }
            }
          }
        }
      }
    }
  }

  /*
   * RTCPeerConnection IceCandidate callback
   */
  private onIceCandidate(event: RTCPeerConnectionIceEvent) {
    logI(LOGGING_TAG, "onIceCandidate %o", event.candidate && event.candidate.toJSON());

    if (event && event.candidate) {
      this._iceCandidates.push(event.candidate);

      this.webSocketNegotiation.postData(
        "addIceCandidate",
        { body: event.candidate, peerid: this.pc.peerid },
        (response) => {
          if (isErrorResponse(response)) {
            logE(LOGGING_TAG, "Failed to send candidate to server: %o", response);
            eventEmitter.emit(PlayerEvents.ERROR, response.error.message);
            if (this._onFatalErrorResponse) {
              this.onFatalErrorResponse(response);
            }
            return;
          }
          logD(LOGGING_TAG, "IceCandidate added");
        },
      );
    } else if (!REMOVE_GET_ICE_CANDIDATE(this.webSocketNegotiation.streamerVersion)) {
      this.onIceCandidateEnded();
    }
  }

  private onIceCandidateEnded() {
    logD(LOGGING_TAG, "onIceCandidateEnded");
    if (this._gatheringTimeout !== undefined) {
      logD(LOGGING_TAG, "Clearing gathering timeout");
      clearTimeout(this._gatheringTimeout);
      this._gatheringTimeout = undefined;
    }

    if (REMOVE_GET_ICE_CANDIDATE(this.webSocketNegotiation.streamerVersion)) {
      return;
    }

    this.webSocketNegotiation.postData(
      "getIceCandidate",
      { peerid: this.pc.peerid },
      (data) => this.onReceiveCandidate(data),
    );
    logD(LOGGING_TAG, "End of candidates.");
  }

  /*
   * RTCPeerConnection AddTrack callback
   */
  private onAddTrack(event: RTCTrackEvent) {
    logD(LOGGING_TAG, "Remote track added: %o", event);

    // collect up to 1 audio and 1 video track from media streams
    if (!this.stream) {
      this.stream = new MediaStream();
    }
    let tracks = event.streams[0].getVideoTracks();
    if (tracks.length && 0 == this.stream.getVideoTracks().length) {
      this.stream.addTrack(tracks[0]);
      logD(LOGGING_TAG, "Collected video stream track from media stream", tracks[0]);
    }
    tracks = event.streams[0].getAudioTracks();
    if (tracks.length && 0 == this.stream.getAudioTracks().length) {
      this.stream.addTrack(tracks[0]);
      logD(LOGGING_TAG, "Collected audio stream track from media stream", tracks[0]);
    }

    const streamTracks = this.stream.getTracks();
    let foundVideo = false;
    let foundAudio = this.mute;
    if (streamTracks.length > 0) {
      for (let idx = 0; idx < streamTracks.length; idx++) {
        const track = streamTracks[idx];
        if (track.kind === "video") {
          foundVideo = true;
          if (foundAudio) {
            break;
          }
        } else if (track.kind === "audio") {
          foundAudio = true;
          if (foundVideo) {
            break;
          }
        }
      }
    }

    logD(LOGGING_TAG, "Found video: %o, audio: %o", foundVideo, foundAudio);

    if (!foundAudio || !foundVideo) {
      return;
    }

    let container: HTMLDivElement | undefined;
    if (!this.videoElement) {
      logD(LOGGING_TAG, "Create videoElement");

      if (this.useDesktopCrop) {
        container = document.createElement("div");
      }
      const videoelt = document.createElement("video");
      videoelt.id = this.videoTag;
      videoelt.muted = false;
      videoelt.preload = "none";
      videoelt.autoplay = false;
      videoelt.playsInline = true;
      videoelt.style.display = "block";
      if (this.useDesktopCrop) {
        videoelt.setAttribute("data-scale", "local");
      }
      if (this.preferedWindowMode === "windowed" && this.mouseInputMode === "relative") {
        videoelt.addEventListener("mousedown", () => {
          if (!!!document.pointerLockElement) {
            if (isSafari()) {
              this.safariRequestPointerLock();
            } else {
              this.requestPointerLock();
            }
          }
        });
      }

      videoelt.onplay = () => {
        logD(LOGGING_TAG, "Playing...");
        this.lastFramesDecodedTimestampSecond = new Date().getTime() / 1000;
        this.initMetrics();
        this.createInputs();
        eventEmitter.emit(PlayerEvents.IS_PLAYING);

        if (this.useDesktopCrop) {
          this.debouncedResize = debounce(this.onWindowResize, 200, {
            leading: true,
            trailing: true,
          });
          window.addEventListener("resize", this.debouncedResize);

          videoelt.style.transformOrigin = "top left";

          this.resizeAndCenter();
        }
      };
      if (this.useDesktopCrop) {
        videoelt.addEventListener("resize", (e) => {
          logD(
            LOGGING_TAG,
            "Video resized %o",
            (e.target as HTMLVideoElement).getBoundingClientRect(),
          );
          this.resizeAndCenter();
        });
      }
      videoelt.onpause = () => {
        logD(LOGGING_TAG, "Paused...");
        if (this.videoElement) {
          if (!this.inDisconnect) {
            eventEmitter.emit(PlayerEvents.PAUSED);
          }
          if (this.metricsTimeout !== undefined) {
            clearTimeout(this.metricsTimeout);
            this.metricsTimeout = undefined;
          }
        }
      };

      videoelt.onsuspend = () => {
        logD(LOGGING_TAG, "Suspended...");
      };

      videoelt.addEventListener("loadeddata", () => {
        logD(LOGGING_TAG, "on loadeddata", videoelt.readyState);
      });

      videoelt.addEventListener(
        "error",
        (e) => {
          logE(LOGGING_TAG, "Video got an error: %o", e);
          eventEmitter.emit(PlayerEvents.ERROR, e);
        },
        true,
      );

      videoelt.onended = () => {
        logD(LOGGING_TAG, "Ended...");
      };
      if (this.useDesktopCrop && container) {
        container.appendChild(videoelt);
        this.contentNode.appendChild(container);
        this.container = container;
      } else {
        this.contentNode.appendChild(videoelt);
      }

      this.videoElement = videoelt;
      this.videoElement.srcObject = this.stream;
    }
  }

  private play() {
    this.videoElement
      .play()
      .then(() => {
        logD(LOGGING_TAG, "Play success !");
        this.hasPlayed = true;
      })
      .catch((e) => {
        logE(LOGGING_TAG, "Caught error play %o", e);
        //WebRTCClient.js:211 Unmuting failed and the element was paused instead because the user didn't interact with the document before. https://goo.gl/xX8pDD
        eventEmitter.emit(PlayerEvents.ERROR, e);
      });
  }

  /*
   * WS /call callback
   */
  private onReceiveCall(dataJson) {
    logI(LOGGING_TAG, "onReceiveCall. Offer: %o", dataJson);

    if (isErrorResponse(dataJson)) {
      eventEmitter.emit(PlayerEvents.ERROR, dataJson.error.message);
      if (this._onFatalErrorResponse) {
        this.onFatalErrorResponse(dataJson);
      }
      return;
    }

    this.pc
      .setRemoteDescription(new RTCSessionDescription(dataJson))
      .then(() => {
        logD(LOGGING_TAG, "setRemoteDescription ok");
      })
      .catch((error) => {
        logE(LOGGING_TAG, "setRemoteDescription error: %o", error);
        eventEmitter.emit(PlayerEvents.ERROR, error);
      });
  }

  /**
   * WS /getIceCandidate callback
   * @deprecated since msk
   */
  private onReceiveCandidate(dataJson) {
    logI(LOGGING_TAG, "Candidate received: %o", dataJson);

    if (isErrorResponse(dataJson)) {
      eventEmitter.emit(PlayerEvents.ERROR, dataJson.error.message);
      if (this._onFatalErrorResponse) {
        this.onFatalErrorResponse(dataJson);
      }
      return;
    }
    if (dataJson) {
      for (let i = 0; i < dataJson.length; i++) {
        const candidate = new RTCIceCandidate(dataJson[i]);
        logD(LOGGING_TAG, "Adding ICE candidate from remote: %o", candidate.toJSON());
        this.addRemoteIceCandidate(candidate);
      }
    }
  }

  public addRemoteIceCandidate(candidate: RTCIceCandidate) {
    return this.pc
      .addIceCandidate(candidate)
      .then(() => {
        logD(LOGGING_TAG, "addRemoteIceCandidate (%o) OK", candidate.toJSON());
      })
      .catch((error) => {
        logE(
          LOGGING_TAG,
          "addRemoteIceCandidate (%o) error: %o",
          candidate.toJSON(),
          error,
        );
        eventEmitter.emit(PlayerEvents.ERROR, error);
      });
  }

  private metricsTimestamp:
    | {
        video: number | undefined;
        audio: number | undefined;
      }
    | undefined;

  private isVideoStat(stat: any) {
    return (
      stat.type === "inbound-rtp" &&
      (stat.kind === "video" ||
        (stat.ssrc && stat.mediaType === "video") ||
        stat.frameHeight !== 0) // => to deal WebKit wpe full gstreamer base?
    );
  }
  private isAudioStat(stat: any) {
    return (
      stat.type === "inbound-rtp" &&
      (stat.kind === "audio" ||
        (stat.ssrc && stat.mediaType === "audio") ||
        stat.frameHeight === undefined) // => to deal WebKit wpe full gstreamer base?
    );
  }

  private initMetrics() {
    if (this._statsInterval === -1) {
      logD(LOGGING_TAG, "Metrics disabled");
      return;
    }
    if (this.embedded) {
      this.pc
        .getStats()
        .then((stats: unknown) => {
          eventEmitter.emit(PlayerEvents.METRIC, stats);
        })
        .catch((e) => {
          logE(LOGGING_TAG, "Caught error getting stats :%o", e);
        });
    } else {
      this.pc.getStats().then((stats: RTCStatsReport) => {
        const metric: GameStreamMetric = {
          audio: {},
          video: {},
          network: {},
          connection: {},
        };
        const pair = this.getSelectedRTCIceCandidatePair();
        if (pair) {
          metric.connection.tags = {
            localProtocol: pair.local.protocol,
            localCandidateType: pair.local.type,
            remoteCandidateType: pair.remote.type,
            remoteProtocol: pair.remote.protocol,
          };
        }

        if (this.metricsTimestamp === undefined) {
          this.metricsTimestamp = {
            audio: undefined,
            video: undefined,
          };
          return;
        }

        stats.forEach((stat) => {
          TRACE_METRICS && logD(LOGGING_TAG, "Got stat %o", stat);
          if (this.isVideoStat(stat) && this.metricsTimestamp.video === undefined) {
            this.metricsTimestamp.video = Math.ceil(stat.timestamp);
            return;
          }
          if (this.isAudioStat(stat) && this.metricsTimestamp.audio === undefined) {
            this.metricsTimestamp.audio = Math.ceil(stat.timestamp);
            return;
          }

          if (stat.currentRoundTripTime !== undefined) {
            metric.network.rtt = stat.currentRoundTripTime * 1000;
          }
          if (this.isAudioStat(stat)) {
            logD(LOGGING_TAG, "Proceed audio stat: %o", stat);
            let jitter =
              typeof stat.jitter === "string" ? parseInt(stat.jitter, 10) : stat.jitter;
            const packetsLost =
              typeof stat.packetsLost === "string"
                ? parseInt(stat.packetsLost, 10)
                : stat.packetsLost;
            const bytesReceived =
              typeof stat.bytesReceived === "string"
                ? parseInt(stat.bytesReceived, 10)
                : stat.bytesReceived;
            const packetsReceived =
              typeof stat.packetsReceived === "string"
                ? parseInt(stat.packetsReceived, 10)
                : stat.packetsReceived;
            jitter = Math.round(jitter * 90000); /*As requested by AS*/

            metric.audio.jitter = jitter;
            metric.audio.pktLost = packetsLost;
            metric.audio.bytesRecv = bytesReceived;
            metric.audio.pktRecv1 = packetsReceived;
            if (stat.timestamp && this.metricsTimestamp.audio) {
              const now = Math.ceil(stat.timestamp);
              const diffInMs = now - this.metricsTimestamp.audio;
              if (diffInMs !== 0) {
                const bytesPerS = bytesReceived / (diffInMs / 1000);
                metric.audio.bitrate = Math.round(bytesPerS * 8);
              }
            }
          }
          if (this.isVideoStat(stat)) {
            logD(LOGGING_TAG, "Proceed video stat: %o", stat);
            let jitter =
              typeof stat.jitter === "string" ? parseInt(stat.jitter, 10) : stat.jitter;
            jitter = Math.round(jitter * 90000); /*As requested by AS*/

            // https://blacknut.atlassian.net/browse/CA-2545
            const additionalStats: { key: string; type?: "int" | "float" }[] = [
              { key: "qpSum" },
              { key: "totalInterFrameDelay", type: "float" },
              { key: "totalSquaredInterFrameDelay", type: "float" },
              { key: "jitterBufferDelay", type: "float" },
              { key: "jitterBufferEmittedCount" },
              { key: "fecPacketsReceived" },
              { key: "fecPacketsDiscarded" },
              { key: "framesDecoded" },
              { key: "framesReceived" },
              { key: "nackCount" },
              { key: "firCount" },
              { key: "pliCount" },
              { key: "sliCount" },
            ];
            additionalStats.forEach((additionalStat) => {
              if (stat[additionalStat.key] !== undefined) {
                try {
                  const val =
                    typeof stat[additionalStat.key] === "string"
                      ? additionalStat.type === "float"
                        ? parseFloat(stat[additionalStat.key])
                        : parseInt(stat[additionalStat.key], 10)
                      : additionalStat.type === "float"
                      ? stat[additionalStat.key]
                      : Math.round(parseInt(stat[additionalStat.key]));
                  metric.video[additionalStat.key] = val;

                  // https://blacknut.atlassian.net/browse/CA-2661
                  if (additionalStat.key === "framesDecoded" && val !== undefined) {
                    const currentTimestampSecond = new Date().getTime() / 1000;
                    if (val !== 0) {
                      this.lastFramesDecodedTimestampSecond =
                        new Date().getTime() / 1000;
                    } else if (
                      currentTimestampSecond - this.lastFramesDecodedTimestampSecond >
                      20
                    ) {
                      logW(
                        LOGGING_TAG,
                        "More than 20s since last framesDecoded: last:%o, current:%o",
                        this.lastFramesDecodedTimestampSecond,
                        currentTimestampSecond,
                      );
                      this.onDisconnect();
                    }
                  }
                } catch (e) {
                  logE(
                    LOGGING_TAG,
                    "Error parsing stat with key %o",
                    additionalStat.key,
                    e,
                  );
                }
              }
            });

            const packetsLost =
              typeof stat.packetsLost === "string"
                ? parseInt(stat.packetsLost, 10)
                : stat.packetsLost;
            const bytesReceived =
              typeof stat.bytesReceived === "string"
                ? parseInt(stat.bytesReceived, 10)
                : stat.bytesReceived;
            const packetsReceived =
              typeof stat.packetsReceived === "string"
                ? parseInt(stat.packetsReceived, 10)
                : stat.packetsReceived;

            metric.video.jitter = jitter;
            metric.video.pktLost = packetsLost;
            metric.video.bytesRecv = bytesReceived;
            metric.video.pktRecv1 = packetsReceived;

            if (stat.timestamp && this.metricsTimestamp.video) {
              const now = Math.ceil(stat.timestamp);
              const diffInMs = now - this.metricsTimestamp.video;
              if (diffInMs !== 0) {
                const bytesPerS = bytesReceived / (diffInMs / 1000);
                metric.video.bitrate = Math.round(bytesPerS * 8);
              }
            }
          }

          if (stat.type === "candidate-pair" && stat.nominated) {
            this.addCandidatePairStats(metric, stat);
          }
        });
        eventEmitter.emit(PlayerEvents.METRIC, metric);
      });
    }
    this.metricsTimeout = setTimeout(() => {
      this.initMetrics.bind(this)();
    }, this._statsInterval);
  }

  private addCandidatePairStats(metric: Partial<GameStreamMetric>, stat: any) {
    logD(LOGGING_TAG, "Selected candidate pair stat: %o", stat);
    metric.connection = metric.connection || {};
    const bytesReceived = safeParseInt(stat, "bytesReceived");
    if (bytesReceived !== undefined) {
      metric.connection.bytesReceived = bytesReceived;
    }

    const bytesSent = safeParseInt(stat, "bytesSent");
    if (bytesSent !== undefined) {
      metric.connection.bytesSent = bytesSent;
    }

    const packetsReceived = safeParseInt(stat, "packetsReceived");
    if (packetsReceived !== undefined) {
      metric.connection.packetsReceived = packetsReceived;
    }

    const packetsSent = safeParseInt(stat, "packetsSent");
    if (packetsSent !== undefined) {
      metric.connection.packetsSent = packetsSent;
    }
    const currentRoundTripTime = safeParseFloat(stat, "currentRoundTripTime");
    if (currentRoundTripTime !== undefined) {
      metric.connection.currentRoundTripTime = Math.floor(currentRoundTripTime * 1000);
    }

    const availableOutgoingBitrate = safeParseInt(stat, "availableOutgoingBitrate");
    if (availableOutgoingBitrate !== undefined) {
      metric.connection.availableOutgoingBitrate = availableOutgoingBitrate;
    }
  }

  private resizeAndCenter = () => {
    if (!isHTMLVideoElememt(this.videoElement)) {
      return;
    }
    const inAspectRatio = this._streamingSize.width / this._streamingSize.height;
    this.videoElement.style.display = "none";

    //reset
    this.container.style.width = `0px`;
    this.container.style.height = `0px`;
    this.videoElement.style.marginTop = `0px`;
    this.videoElement.style.marginLeft = `0px`;

    requestAnimationFrame(() => {
      if (!isHTMLVideoElememt(this.videoElement)) {
        return;
      }
      const containerWidth = this.contentNode.getBoundingClientRect().width;
      const containerHeight = this.contentNode.getBoundingClientRect().height;
      logD(LOGGING_TAG, "Container size: %ox%o", containerWidth, containerHeight);
      logD(
        LOGGING_TAG,
        "Streaming size: %ox%o",
        this._streamingSize.width,
        this._streamingSize.height,
      );
      let finalWidth = 0;
      let finalHeight = 0;
      if (this._streamingSize.width > this._streamingSize.height) {
        // landscape
        finalWidth = containerWidth;
        finalHeight = finalWidth / inAspectRatio;
        if (finalHeight > containerHeight) {
          finalHeight = containerHeight;
          finalWidth = finalHeight * inAspectRatio;
        }
      } else {
        finalHeight = containerHeight;
        finalWidth = finalHeight * inAspectRatio;
      }
      logD(LOGGING_TAG, "Targetted size: %ox%o", finalWidth, finalHeight);

      const scaleX = finalWidth / this._streamingSize.width;
      const scaleY = finalHeight / this._streamingSize.height;
      this.videoElement.style.transform = `scale(${scaleX}, ${scaleY})`;
      logD(LOGGING_TAG, "Scale: %ox%o", scaleX, scaleY);

      this.videoElement.style.display = "block";

      this.container.style.width = `${containerWidth}px`;
      this.container.style.height = `${containerHeight}px`;

      // center it
      const videoWidth = this.videoElement.getBoundingClientRect().width;
      const videoHeight = this.videoElement.getBoundingClientRect().height;

      logD(LOGGING_TAG, "Video size: %ox%o", videoWidth, videoHeight);
      this.videoElement.style.marginTop = `${(containerHeight - videoHeight) / 2}px`;
      this.videoElement.style.marginLeft = `${(containerWidth - videoWidth) / 2}px`;
    });
  };
  private onWindowResize = () => {
    this.resizeAndCenter();
  };

  public sendUnicodeString(s: string) {
    this.webSocketManager.sendDataUnicode(1, s);
  }
}
